import { useKeyboardLayoutStore } from '../store/keyboardLayoutSlice';

interface NavigatorKeyboard {
  getLayoutMap(): Promise<Map<string, string>>;
  addEventListener?(type: 'layoutchange', listener: () => void): void;
  removeEventListener?(type: 'layoutchange', listener: () => void): void;
}

interface NavigatorWithKeyboard extends Navigator {
  keyboard?: NavigatorKeyboard;
}

const LETTER_CODES = new Set([
  'KeyA',
  'KeyB',
  'KeyC',
  'KeyD',
  'KeyE',
  'KeyF',
  'KeyG',
  'KeyH',
  'KeyI',
  'KeyJ',
  'KeyK',
  'KeyL',
  'KeyM',
  'KeyN',
  'KeyO',
  'KeyP',
  'KeyQ',
  'KeyR',
  'KeyS',
  'KeyT',
  'KeyU',
  'KeyV',
  'KeyW',
  'KeyX',
  'KeyY',
  'KeyZ',
]);

const LATIN_CHAR_TO_CODE: Record<string, string> = {
  a: 'KeyA',
  b: 'KeyB',
  c: 'KeyC',
  d: 'KeyD',
  e: 'KeyE',
  f: 'KeyF',
  g: 'KeyG',
  h: 'KeyH',
  i: 'KeyI',
  j: 'KeyJ',
  k: 'KeyK',
  l: 'KeyL',
  m: 'KeyM',
  n: 'KeyN',
  o: 'KeyO',
  p: 'KeyP',
  q: 'KeyQ',
  r: 'KeyR',
  s: 'KeyS',
  t: 'KeyT',
  u: 'KeyU',
  v: 'KeyV',
  w: 'KeyW',
  x: 'KeyX',
  y: 'KeyY',
  z: 'KeyZ',
};

let layoutMap: Map<string, string> | null = null;

async function fetchLayout(kbd: NavigatorKeyboard): Promise<void> {
  try {
    layoutMap = await kbd.getLayoutMap();
  } catch {
    layoutMap = null;
  }
  useKeyboardLayoutStore.getState().bump();
}

export async function initKeyboardLayout(): Promise<void> {
  const kbd = (navigator as NavigatorWithKeyboard).keyboard;
  if (!kbd || typeof kbd.getLayoutMap !== 'function') {
    return;
  }
  await fetchLayout(kbd);
  kbd.addEventListener?.('layoutchange', () => {
    void fetchLayout(kbd);
  });
}

export function resolveDisplayCode(code: string): string {
  if (!layoutMap || !LETTER_CODES.has(code)) {
    return code;
  }
  const ch = layoutMap.get(code);
  if (!ch || ch.length !== 1) {
    return code;
  }
  return LATIN_CHAR_TO_CODE[ch.toLowerCase()] ?? code;
}

export function resolveDisplayChar(code: string): string | null {
  if (!layoutMap) {
    return null;
  }
  return layoutMap.get(code) ?? null;
}
